// ============================================================
// PRODUCT CATALOGUE — single source of truth
// ============================================================
// To ADD a product:
//   1. Drop images in public/assets/products/<slug>/
//      (main.jpg, gallery-1.jpg, gallery-2.jpg …)
//   2. Add one entry to the `products` array below.
//   3. Save. The product card, detail page, URL, breadcrumb,
//      related products and sitemap update automatically.
//
// To REMOVE a product:
//   Delete its entry (or set `active: false`).
//
// To CHANGE a description / features / SEO for an existing
// product: edit its object below.
//
// No React components need editing for any of the above.
// ============================================================

const CUSTOMIZATION_DEFAULT =
  "Dimensions, configuration and quantity can be discussed according to your requirement. Custom requirements are welcome.";

// Helper: fills sensible defaults
const p = (o) => ({
  active: true,
  featured: false,
  gallery: [],
  specifications: [],
  tags: [],
  customization: CUSTOMIZATION_DEFAULT,
  ...o,
  // Convention: main image at /assets/products/<slug>/main.jpg
  image: o.image || `/assets/products/${o.slug}/main.jpeg`,
});

// ---- Categories (used for grouping + filters) ----
export const categories = [
  {
    id: "corrugated",
    name: "Corrugated Packaging",
    description:
      "Corrugated boxes and mono cartons built around product dimensions, handling and dispatch requirements.",
    slugs: ["corrugated-boxes", "mono-cartons"],
  },
  {
    id: "pp",
    name: "PP Packaging",
    description:
      "Reusable polypropylene bins and boxes for material handling and storage.",
    slugs: ["pp-bins-boxes"],
  },
  {
    id: "protective",
    name: "Protective Packaging",
    description:
      "Cushioning, moisture and static protection for sensitive components.",
    slugs: [
      "epe-foam-fitments",
      "bubble-rolls",
      "anti-static-bags",
      "humidity-indicators",
      "silica-gel",
      "activated-carbon",
    ],
  },
  {
    id: "accessories",
    name: "Packaging Accessories & Materials",
    description:
      "Consumables and reinforcement materials that support day-to-day packaging operations.",
    slugs: ["stretch-film", "strapping-rolls", "paper-edge-protectors", "angle-boards"],
  },
  {
    id: "pallets",
    name: "Pallets",
    description:
      "Plastic and wooden pallets suited to storage, warehousing and dispatch handling.",
    slugs: ["plastic-pallets", "wooden-pallets"],
  },
];

// ---- Products ----
export const products = [
  p({
    id: "corrugated-boxes",
    name: "Corrugated Boxes",
    slug: "corrugated-boxes",
    category: "Corrugated Packaging",
    tags: ["Customizable", "Industrial Packaging"],
    shortDescription:
      "Corrugated boxes engineered around your product dimensions, weight and dispatch requirements.",
    fullDescription:
      "Corrugated boxes are supplied in configurations tailored to product size, handling method and transportation profile. Sizes, ply construction and print requirements can be discussed based on your application.",
    features: [
      "Sizes tailored to product dimensions",
      "Suitable for a range of industrial and dispatch applications",
      "Configurations discussed based on your requirement",
      "Available in requirement-based quantities",
    ],
    applications: [
      "Product packaging for dispatch",
      "Storage and handling",
      "Component and assembly packaging",
    ],
    seoTitle: "Corrugated Boxes — Rudraksh Corrugation Solutions",
    seoDescription:
      "Corrugated boxes tailored to your dimensions, quantities and application. Supplied by Rudraksh Corrugation Solutions, Greater Noida.",
    keywords: ["corrugated boxes", "packaging boxes", "carton boxes", "greater noida"],
    relatedProducts: ["mono-cartons", "paper-edge-protectors", "angle-boards"],
    featured: true,
  }),
  p({
    id: "mono-cartons",
    name: "Mono Cartons",
    slug: "mono-cartons",
    category: "Corrugated Packaging",
    tags: ["Customizable"],
    shortDescription:
      "Mono cartons suitable for product packaging where a clean, single-wall carton is required.",
    fullDescription:
      "Mono cartons are supplied to customer specification for product packaging, kitting and unit-of-sale requirements. Print and finish options can be discussed based on the application.",
    features: [
      "Requirement-based sizes",
      "Suitable for retail-style and product packaging",
      "Customization discussed on enquiry",
    ],
    applications: ["Retail-style product packaging", "Kitting", "Component packaging"],
    seoTitle: "Mono Cartons — Rudraksh Corrugation Solutions",
    seoDescription:
      "Mono cartons customized to your product dimensions and packaging requirements.",
    keywords: ["mono cartons", "product cartons"],
    relatedProducts: ["corrugated-boxes"],
  }),
  p({
    id: "pp-bins-boxes",
    name: "PP Bins & Boxes",
    slug: "pp-bins-boxes",
    category: "PP Packaging",
    tags: ["Customizable", "Reusable"],
    shortDescription:
      "Durable polypropylene bins and boxes designed around your handling, storage and transportation requirements.",
    fullDescription:
      "PP bins and boxes are supplied in configurations that suit warehousing, in-plant material handling and transportation of components. Dimensions and quantities are discussed based on the intended application.",
    features: [
      "Reusable material-handling solution",
      "Requirement-based dimensions",
      "Suitable for industrial storage and movement",
      "Available according to customer requirement",
    ],
    applications: [
      "In-plant material handling",
      "Component storage",
      "Warehousing and dispatch",
    ],
    seoTitle: "PP Bins & Boxes — Rudraksh Corrugation Solutions",
    seoDescription:
      "PP bins and boxes for industrial storage and material handling. Customized dimensions available.",
    keywords: ["pp bins", "pp boxes", "plastic bins"],
    relatedProducts: ["plastic-pallets", "epe-foam-fitments"],
    featured: true,
  }),
  p({
    id: "stretch-film",
    name: "Stretch Film",
    slug: "stretch-film",
    category: "Packaging Accessories & Materials",
    tags: ["Industrial Packaging"],
    shortDescription:
      "Stretch film for pallet wrapping and load containment during storage and dispatch.",
    fullDescription:
      "Stretch film is supplied for pallet wrapping and load containment applications. Width, thickness and quantity requirements can be discussed on enquiry.",
    features: ["Load containment for pallets", "Available in requirement-based quantities"],
    applications: ["Pallet wrapping", "Load containment"],
    seoTitle: "Stretch Film — Rudraksh Corrugation Solutions",
    seoDescription: "Stretch film for pallet wrapping and load containment.",
    keywords: ["stretch film", "pallet wrap"],
    relatedProducts: ["strapping-rolls", "wooden-pallets", "plastic-pallets"],
    featured: true,
  }),
  p({
    id: "silica-gel",
    name: "Silica Gel",
    slug: "silica-gel",
    category: "Protective Packaging",
    tags: ["Protective Packaging"],
    shortDescription:
      "Silica gel desiccants used to control moisture inside packed goods during storage and transit.",
    fullDescription:
      "Silica gel is supplied for moisture control inside packaging. Pack size and quantities can be discussed based on the volume being protected.",
    features: ["Moisture absorption inside packaging", "Requirement-based pack sizes"],
    applications: ["Component and part protection", "Export packaging", "Sensitive goods"],
    seoTitle: "Silica Gel — Rudraksh Corrugation Solutions",
    seoDescription: "Silica gel desiccants for moisture control in packaging.",
    keywords: ["silica gel", "desiccant"],
    relatedProducts: ["humidity-indicators", "anti-static-bags", "activated-carbon"],
  }),
  p({
    id: "activated-carbon",
    name: "Activated Carbon",
    slug: "activated-carbon",
    category: "Protective Packaging",
    tags: ["Protective Packaging"],
    shortDescription:
      "Activated carbon used in packaging to help absorb odours and unwanted vapours during storage and transit.",
    fullDescription:
      "Activated carbon is supplied for use alongside packaging where absorption of odours or vapours is required. Pack sizes and quantities can be discussed based on your application.",
    features: ["Absorption for odours and vapours", "Requirement-based pack sizes"],
    applications: [
      "Sensitive-goods packaging",
      "Export packaging",
      "Storage where vapour or odour control is needed",
    ],
    seoTitle: "Activated Carbon — Rudraksh Corrugation Solutions",
    seoDescription:
      "Activated carbon for use in packaging where odour and vapour absorption is required.",
    keywords: ["activated carbon", "carbon absorbent"],
    relatedProducts: ["silica-gel", "humidity-indicators", "anti-static-bags"],
  }),
  p({
    id: "strapping-rolls",
    name: "Strapping Rolls",
    slug: "strapping-rolls",
    category: "Packaging Accessories & Materials",
    shortDescription:
      "Strapping rolls used for securing cartons and palletized loads before dispatch.",
    fullDescription:
      "Strapping rolls are supplied for load securing applications. Roll size and quantity requirements can be discussed based on your usage.",
    features: ["Load securing for cartons and pallets", "Requirement-based quantities"],
    applications: ["Load securing", "Dispatch preparation"],
    seoTitle: "Strapping Rolls — Rudraksh Corrugation Solutions",
    seoDescription: "Strapping rolls for cartons and palletized loads.",
    keywords: ["strapping rolls", "packaging strap"],
    relatedProducts: ["stretch-film", "corrugated-boxes"],
  }),
  p({
    id: "humidity-indicators",
    name: "Humidity Indicators",
    slug: "humidity-indicators",
    category: "Protective Packaging",
    tags: ["Protective Packaging"],
    shortDescription:
      "Humidity indicators used alongside desiccants to monitor moisture conditions inside packaging.",
    fullDescription:
      "Humidity indicators help verify moisture conditions inside sealed packaging. Availability is based on customer requirement.",
    features: ["Moisture-condition monitoring", "Used with desiccants"],
    applications: ["Sensitive component packaging", "Export packaging"],
    seoTitle: "Humidity Indicators — Rudraksh Corrugation Solutions",
    seoDescription: "Humidity indicators for moisture-sensitive packaging.",
    keywords: ["humidity indicator", "humidity indicator card"],
    relatedProducts: ["silica-gel", "anti-static-bags"],
  }),
  p({
    id: "paper-edge-protectors",
    name: "Paper Edge Protectors (Angle Boards)",
    slug: "paper-edge-protectors",
    category: "Packaging Accessories & Materials",
    shortDescription:
      "Paper edge protectors or Angle boards used to reinforce edges and corners of palletized loads and cartons.",
    fullDescription:
      "Paper edge protectors reinforce the edges of packed goods during storage and transit. Sizes are discussed based on load and application.",
    features: ["Edge and corner reinforcement", "Requirement-based sizes"],
    applications: ["Palletized loads", "Carton reinforcement", "Load securing"],
    seoTitle: "Paper Edge Protectors (Angle boards) — Rudraksh Corrugation Solutions",
    seoDescription: "Paper edge protectors for pallets and cartons.",
    keywords: ["edge protectors", "corner protectors", "angle boards", "corner boards"],
    relatedProducts: ["angle-boards", "stretch-film", "corrugated-boxes"],
  }),
  // p({
  //   id: "angle-boards",
  //   name: "Angle Boards",
  //   slug: "angle-boards",
  //   category: "Packaging Accessories & Materials",
  //   shortDescription:
  //     "Angle boards for corner protection and load reinforcement on pallets and cartons.",
  //   fullDescription:
  //     "Angle boards provide corner reinforcement for palletized loads and stacked cartons. Length and profile are discussed based on requirement.",
  //   features: ["Corner reinforcement", "Requirement-based lengths"],
  //   applications: ["Palletized loads", "Stacked cartons"],
  //   seoTitle: "Angle Boards — Rudraksh Corrugation Solutions",
  //   seoDescription: "Angle boards for pallet and carton reinforcement.",
  //   keywords: ["angle boards", "corner boards"],
  //   relatedProducts: ["paper-edge-protectors", "stretch-film"],
  // }),
  p({
    id: "plastic-pallets",
    name: "Plastic Pallets",
    slug: "plastic-pallets",
    category: "Pallets",
    tags: ["Reusable"],
    shortDescription:
      "Plastic pallets suited to warehousing and handling requirements where a reusable platform is preferred.",
    fullDescription:
      "Plastic pallets are supplied for warehousing, storage and dispatch handling. Dimensions and configurations can be discussed based on your requirement.",
    features: ["Reusable platform", "Requirement-based sizes"],
    applications: ["Warehousing", "Material handling", "Dispatch"],
    seoTitle: "Plastic Pallets — Rudraksh Corrugation Solutions",
    seoDescription: "Plastic pallets for warehousing and material handling.",
    keywords: ["plastic pallets", "hdpe pallets"],
    relatedProducts: ["wooden-pallets", "pp-bins-boxes"],
    featured: true,
  }),
  p({
    id: "wooden-pallets",
    name: "Wooden Pallets",
    slug: "wooden-pallets",
    category: "Pallets",
    shortDescription:
      "Wooden pallets for storage, dispatch and general handling applications.",
    fullDescription:
      "Wooden pallets are supplied for warehousing and dispatch. Dimensions and configurations can be discussed based on your load and handling method.",
    features: ["Requirement-based sizes", "Suitable for general handling"],
    applications: ["Warehousing", "Dispatch", "Load handling"],
    seoTitle: "Wooden Pallets — Rudraksh Corrugation Solutions",
    seoDescription: "Wooden pallets for storage and dispatch handling.",
    keywords: ["wooden pallets"],
    relatedProducts: ["plastic-pallets", "stretch-film"],
  }),
  p({
    id: "bubble-rolls",
    name: "Bubble Rolls",
    slug: "bubble-rolls",
    category: "Protective Packaging",
    tags: ["Protective Packaging"],
    shortDescription:
      "Bubble rolls used for cushioning during packing, storage and transit.",
    fullDescription:
      "Bubble rolls provide cushioning around packed products. Width, length and quantities can be discussed based on your application.",
    features: ["Cushioning and surface protection", "Requirement-based quantities"],
    applications: ["Product cushioning", "Wrapping fragile items"],
    seoTitle: "Bubble Rolls — Rudraksh Corrugation Solutions",
    seoDescription: "Bubble rolls for cushioning and surface protection.",
    keywords: ["bubble rolls", "bubble wrap"],
    relatedProducts: ["epe-foam-fitments", "corrugated-boxes"],
  }),
  p({
    id: "epe-foam-fitments",
    name: "EPE Foam Fitments",
    slug: "epe-foam-fitments",
    category: "Protective Packaging",
    tags: ["Customizable", "Protective Packaging"],
    shortDescription:
      "EPE foam fitments profiled around product shapes for cushioning and secure placement inside packaging.",
    fullDescription:
      "EPE foam fitments are supplied as inserts profiled to suit product geometry. Dimensions and profiles are discussed based on the product being protected.",
    features: [
      "Product-profiled fitments",
      "Requirement-based configurations",
      "Cushioning inside packaging",
    ],
    applications: ["Component packaging", "Electronics and assemblies", "Sensitive goods"],
    seoTitle: "EPE Foam Fitments — Rudraksh Corrugation Solutions",
    seoDescription: "EPE foam fitments profiled to your product for protective packaging.",
    keywords: ["epe foam", "foam fitments", "foam inserts"],
    relatedProducts: ["bubble-rolls", "corrugated-boxes", "anti-static-bags"],
    featured: true,
  }),
  p({
    id: "anti-static-bags",
    name: "Anti-Static Bags",
    slug: "anti-static-bags",
    category: "Protective Packaging",
    tags: ["Protective Packaging"],
    shortDescription:
      "Anti-static bags used for packaging static-sensitive components during storage and transit.",
    fullDescription:
      "Anti-static bags are supplied for packaging of static-sensitive components. Sizes and quantities can be discussed on enquiry.",
    features: ["Static-sensitive component protection", "Requirement-based sizes"],
    applications: ["Electronics packaging", "PCB and component protection"],
    seoTitle: "Anti-Static Bags — Rudraksh Corrugation Solutions",
    seoDescription: "Anti-static bags for static-sensitive component packaging.",
    keywords: ["anti-static bags", "esd bags"],
    relatedProducts: ["silica-gel", "humidity-indicators", "epe-foam-fitments"],
  }),
];

// ---------- helpers ----------
export function findProduct(slug) {
  return products.find((x) => x.slug === slug && x.active !== false);
}
export function findRelated(slugs = []) {
  return products.filter((x) => slugs.includes(x.slug) && x.active !== false);
}
export function activeProducts() {
  return products.filter((p) => p.active !== false);
}
